import React from 'react';
import { BnplAttributes } from 'types/CreditOfferTypes';
import { renderContent, renderContentWithParams } from 'utilities/content/content-utilities';
import IconRestore from '@tvg/fuse-icons/lib/Restore';
import IconShoppingBasketAdd from '@tvg/fuse-icons/lib/ShoppingBasketAdd';
import IconPersonAuthenticate from '@tvg/fuse-icons/lib/PersonAuthenticate';
import IconAccountBalanceWallet from '@tvg/fuse-icons/lib/AccountBalanceWallet';
import {
  DialogPanel,
  DialogSection,
  DialogSectionIcon,
  DialogSectionHeading,
  DialogSectionSubHeading,
  DialogList,
  DialogListItem,
  DialogText,
  AprText,
  HowToUseSection,
  HowToUseList,
  HowToUseStep,
  Question,
} from '../../styles/VeryPayDialogStyles';

type Props = {
  active: boolean;
  apr: string;
  bnplAttributes: BnplAttributes[];
  nonCreditAccountUser: boolean;
  transitionDuration: number;
};

function BnplPage(props: Props) {
  const { active, apr, bnplAttributes, nonCreditAccountUser, transitionDuration } = props;

  return (
    <DialogPanel active={active} transitionDuration={transitionDuration} dataTestId="verypay_dialog_bnpl">
      <DialogSection>
        <DialogSectionIcon
          icon={<IconRestore />}
          height="32px"
          width="32px"
          alt={renderContent('verypay.dialog.bnpl.dialogHeading')}
        />

        <DialogSectionHeading
          tag="h3"
          size="4"
          dangerouslySetInnerHTML={{
            __html: renderContent('verypay.dialog.bnpl.subHeading'),
          }}
        />

        <DialogSectionSubHeading tag="h4" size="4">
          {renderContent('verypay.dialog.bnpl.info.heading')}
        </DialogSectionSubHeading>

        <DialogList>
          <DialogListItem>{renderContent('verypay.dialog.bnpl.info.point1')}</DialogListItem>
          <DialogListItem>
            <DialogText>{renderContent('verypay.dialog.bnpl.info.point2')}</DialogText>

            {bnplAttributes.map((attribute, index) => (
              <DialogText
                key={index}
                dangerouslySetInnerHTML={{
                  __html: renderContentWithParams({
                    id: 'verypay.dialog.bnpl.info.point2.minimumOrderValue',
                    params: [attribute.months, attribute.minimumOrderValue],
                  }),
                }}
                data-testid={`verypay_dialog_bnpl_term-${index + 1}`}
              />
            ))}
          </DialogListItem>
          <DialogListItem
            dangerouslySetInnerHTML={{
              __html: renderContent('verypay.dialog.bnpl.info.point3'),
            }}
          />
          <DialogListItem>{renderContent('verypay.dialog.bnpl.info.point4')}</DialogListItem>
          <DialogListItem>{renderContent('verypay.dialog.bnpl.info.point5')}</DialogListItem>
          <DialogListItem>{renderContent('verypay.dialog.bnpl.info.point6')}</DialogListItem>
          <DialogListItem>{renderContent('verypay.dialog.bnpl.info.point7')}</DialogListItem>
          <DialogListItem>{renderContent('verypay.dialog.bnpl.info.point8')}</DialogListItem>
        </DialogList>

        {apr && nonCreditAccountUser && (
          <AprText>
            {renderContentWithParams({
              id: 'verypay.dialog.bnpl.apr',
              params: [apr],
            })}
          </AprText>
        )}
      </DialogSection>

      <HowToUseSection>
        <DialogSectionHeading
          tag="h4"
          size="4"
          dangerouslySetInnerHTML={{
            __html: renderContent('verypay.dialog.bnpl.howToUse.heading'),
          }}
        />

        <HowToUseList>
          <HowToUseStep
            icon={<IconShoppingBasketAdd />}
            height="64px"
            width="64px"
            size="large"
            heading={renderContent('verypay.dialog.bnpl.howToUse.step1.heading')}
            description={renderContent('verypay.dialog.bnpl.howToUse.step1.description')}
          />

          <HowToUseStep
            icon={<IconPersonAuthenticate />}
            height="64px"
            width="64px"
            size="large"
            heading={renderContent('verypay.dialog.bnpl.howToUse.step2.heading')}
            description={renderContent('verypay.dialog.bnpl.howToUse.step2.description')}
          />

          <HowToUseStep
            icon={<IconRestore />}
            height="64px"
            width="64px"
            size="large"
            heading={renderContent('verypay.dialog.bnpl.howToUse.step3.heading')}
            description={renderContent('verypay.dialog.bnpl.howToUse.step3.description')}
          />

          <HowToUseStep
            icon={<IconAccountBalanceWallet />}
            height="64px"
            width="64px"
            size="large"
            heading={renderContent('verypay.dialog.bnpl.howToUse.step4.heading')}
            description={renderContent('verypay.dialog.bnpl.howToUse.step4.description')}
          />
        </HowToUseList>
      </HowToUseSection>

      <DialogSection>
        <Question>
          <DialogSectionSubHeading tag="h4" size="4">
            {renderContent('verypay.dialog.bnpl.questions.question1')}
          </DialogSectionSubHeading>
          <DialogText>{renderContent('verypay.dialog.bnpl.questions.answer1.text1')}</DialogText>
          <DialogText
            dangerouslySetInnerHTML={{
              __html: renderContent('verypay.dialog.bnpl.questions.answer1.text2'),
            }}
          />
        </Question>

        <Question>
          <DialogSectionSubHeading tag="h4" size="4">
            {renderContent('verypay.dialog.bnpl.questions.question2')}
          </DialogSectionSubHeading>
          <DialogText>{renderContent('verypay.dialog.bnpl.questions.answer2')}</DialogText>
        </Question>

        <Question>
          <DialogSectionSubHeading tag="h4" size="4">
            {renderContent('verypay.dialog.bnpl.questions.question3')}
          </DialogSectionSubHeading>
          <DialogText>{renderContent('verypay.dialog.bnpl.questions.answer3.text1')}</DialogText>
          <DialogText
            dangerouslySetInnerHTML={{
              __html: renderContent('verypay.dialog.bnpl.questions.answer3.text2'),
            }}
          />
        </Question>

        <Question>
          <DialogSectionSubHeading tag="h4" size="4">
            {renderContent('verypay.dialog.bnpl.questions.question4')}
          </DialogSectionSubHeading>
          <DialogText>{renderContent('verypay.dialog.bnpl.questions.answer4')}</DialogText>
        </Question>

        <Question>
          <DialogSectionSubHeading tag="h4" size="4">
            {renderContent('verypay.dialog.bnpl.questions.question5')}
          </DialogSectionSubHeading>
          <DialogText>{renderContent('verypay.dialog.bnpl.questions.answer5.text1')}</DialogText>
          <DialogText>{renderContent('verypay.dialog.bnpl.questions.answer5.text2')}</DialogText>
          <DialogText
            dangerouslySetInnerHTML={{
              __html: renderContent('verypay.dialog.bnpl.questions.answer5.text3'),
            }}
          />
        </Question>

        <Question>
          <DialogSectionSubHeading tag="h4" size="4">
            {renderContent('verypay.dialog.bnpl.questions.question6')}
          </DialogSectionSubHeading>
          <DialogText>{renderContent('verypay.dialog.bnpl.questions.answer6')}</DialogText>
        </Question>

        <Question>
          <DialogSectionSubHeading tag="h4" size="4">
            {renderContent('verypay.dialog.bnpl.questions.question7')}
          </DialogSectionSubHeading>
          <DialogText>{renderContent('verypay.dialog.bnpl.questions.answer7')}</DialogText>
        </Question>
      </DialogSection>
    </DialogPanel>
  );
}

export default BnplPage;
